/**
 * MidiStreamStore
 *
 * Pure-data class — no Vue, no DOM.
 *
 * Responsibilities:
 *   - Accumulate raw CC events keyed by "channel:cc"
 *   - Build and maintain rolling time-bucketed display arrays each animation frame
 *   - Expire inactive streams after EXPIRY_MS of silence
 *   - Expose activeKeys, streams, buckets for read-only consumption by the Vue layer
 *
 * Usage:
 *   const store = new MidiStreamStore({ onChange: () => { ... } });
 *   store.start();
 *   store.record(channel, cc, value);
 *   store.clearAll(pauseMs);
 *   store.destroy();
 */

const BUCKET_COUNT = 40;
const WINDOW_MS    = 10_000;   // total x-axis span (10 s)
const BUCKET_MS    = WINDOW_MS / BUCKET_COUNT; // 250 ms per bucket
const EXPIRY_MS    = 30_000;   // stream removed after 30 s of silence

export { BUCKET_COUNT, WINDOW_MS, BUCKET_MS };

export default class MidiStreamStore {
  /**
   * @param {object}   opts
   * @param {function} opts.onChange  Called after every tick or mutation so the
   *                                  Vue layer knows to re-read streams/buckets.
   */
  constructor({ onChange = () => {} } = {}) {
    this._onChange = onChange;

    // keyed by "ch:cc" → { channel, cc, events: [{t, value}], latestValue, expiryTimer }
    this.streams = {};

    // keyed by "ch:cc" → Array(BUCKET_COUNT) of number|null
    this.buckets = {};

    this._rafId  = null;
    this._paused = false;

    // Render-gate state. The rAF loop runs every frame but only recomputes
    // buckets and notifies the Vue layer when the display can actually change,
    // so an idle or unchanged monitor costs ~nothing instead of forcing a full
    // re-render 60×/s.
    this._dirty      = false;  // a new event arrived since the last recompute
    this._lastBucket = -1;     // Date.now()/BUCKET_MS at the last recompute
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  start() {
    const loop = () => {
      // Recompute + re-render only when the display can change:
      //  - a new CC arrived since last frame (_dirty) → near-instant response;
      //  - the 250 ms bucket clock advanced while streams exist → the bars
      //    scroll / trailing edge recedes.
      // Fully idle (no streams, no new data) does zero work and fires no
      // re-render — the previous implementation re-rendered every frame.
      const bucketNow = Math.floor(Date.now() / BUCKET_MS);
      if (this._dirty || (bucketNow !== this._lastBucket && this._hasStreams())) {
        this._dirty      = false;
        this._lastBucket = bucketNow;
        this._recomputeBuckets();
        this._onChange();
      }
      this._rafId = requestAnimationFrame(loop);
    };
    this._rafId = requestAnimationFrame(loop);
  }

  /** Allocation-free "are there any active streams?" check for the hot loop. */
  _hasStreams() {
    for (const _key in this.streams) return true;
    return false;
  }

  destroy() {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    for (const s of Object.values(this.streams)) {
      if (s.expiryTimer) clearTimeout(s.expiryTimer);
    }
    this.streams = {};
    this.buckets = {};
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Sorted list of active "channel:cc" keys. */
  get activeKeys() {
    return Object.keys(this.streams).sort((a, b) => {
      const [ac, acc] = a.split(':').map(Number);
      const [bc, bcc] = b.split(':').map(Number);
      return ac !== bc ? ac - bc : acc - bcc;
    });
  }

  /**
   * Record an incoming CC event.
   * No-op while paused (e.g. during a patch switch).
   */
  record(channel, cc, value) {
    if (this._paused) return;

    const key = `${channel}:${cc}`;
    const t   = Date.now();

    if (!this.streams[key]) {
      this.streams[key] = { channel, cc, events: [], latestValue: value, expiryTimer: null };
    }

    const s = this.streams[key];
    s.events.push({ t, value });
    s.latestValue = value;
    this._dirty = true;  // wake the render gate on the next frame

    if (s.expiryTimer) clearTimeout(s.expiryTimer);
    s.expiryTimer = setTimeout(() => this._removeStream(key), EXPIRY_MS);
  }

  /**
   * Clear all active streams immediately.
   * Optionally suppress incoming events for pauseMs milliseconds (patch switch).
   */
  clearAll(pauseMs = 0) {
    for (const s of Object.values(this.streams)) {
      if (s.expiryTimer) clearTimeout(s.expiryTimer);
    }
    this.streams = {};
    this.buckets = {};
    this._dirty  = false;  // nothing left to recompute; onChange fires now
    this._onChange();

    if (pauseMs > 0) {
      this._paused = true;
      setTimeout(() => { this._paused = false; }, pauseMs);
    }
  }

  // ── Internal ───────────────────────────────────────────────────────────────

  _removeStream(key) {
    const s = this.streams[key];
    if (s?.expiryTimer) clearTimeout(s.expiryTimer);
    delete this.streams[key];
    delete this.buckets[key];
    this._onChange();
  }

  _recomputeBuckets() {
    const now         = Date.now();
    const windowStart = now - WINDOW_MS;
    const newBuckets  = {};

    for (const key of Object.keys(this.streams)) {
      const { events } = this.streams[key];

      // Bucket i covers [windowStart + i*BUCKET_MS, windowStart + (i+1)*BUCKET_MS)
      const result = new Array(BUCKET_COUNT).fill(null);

      for (const { t, value } of events) {
        if (t < windowStart) continue;
        const idx = Math.min(
          BUCKET_COUNT - 1,
          Math.floor((t - windowStart) / BUCKET_MS)
        );
        result[idx] = value;
      }

      // Forward-fill: carry the last known value forward so there's no
      // misleading gap. Buckets before the first data point stay null
      // (rendered as a dim zero bar).
      let lastSeen = null;
      for (let i = 0; i < BUCKET_COUNT; i++) {
        if (result[i] !== null) {
          lastSeen = result[i];
        } else if (lastSeen !== null) {
          result[i] = lastSeen;
        }
      }

      newBuckets[key] = result;

      // Prune events older than two windows (one window buffer for forward-fill).
      this.streams[key].events = events.filter(e => e.t >= windowStart - WINDOW_MS);
    }

    this.buckets = newBuckets;
  }
}
